/**
 * Signal Protocol Core - Crypto Primitives & Double Ratchet
 * Standalone module (no React dependencies)
 */
import nacl from 'tweetnacl'
import { encodeBase64, decodeBase64 } from 'tweetnacl-util'
import { hmac } from '@noble/hashes/hmac'
import { sha256 } from '@noble/hashes/sha256'
import { hkdf } from '@noble/hashes/hkdf'

// ======================== Types ========================

export interface KeyPair { publicKey: Uint8Array; secretKey: Uint8Array }
export interface SignedPreKey { keyId: number; keyPair: KeyPair; signature: Uint8Array }
export interface OneTimePreKey { keyId: number; keyPair: KeyPair }

export interface PreKeyBundle {
  identityKey: Uint8Array
  signedPreKey: Uint8Array
  signedPreKeyId: number
  signedPreKeySignature: Uint8Array
  oneTimePreKey?: Uint8Array
  oneTimePreKeyId?: number
}

export interface X3DHHeader {
  identityKey: Uint8Array
  ephemeralKey: Uint8Array
  signedPreKeyId: number
  oneTimePreKeyId?: number
}

export interface RatchetHeader {
  dhPub: Uint8Array  // 32 bytes
  pn: number         // previous chain length
  n: number          // message number
}

export interface DRState {
  dhSend: KeyPair
  dhRemote: Uint8Array | null
  rk: Uint8Array              // root key
  sCK: Uint8Array | null      // sending chain key
  rCK: Uint8Array | null      // receiving chain key
  sN: number                  // send message number
  rN: number                  // recv message number
  pN: number                  // previous send chain length
  mk: Record<string, string>  // skipped message keys (base64)
}

export interface SessionState {
  rid: string       // recipient ID
  dr: DRState       // double ratchet state
  x3dh: boolean     // X3DH completed
  ts: number        // created timestamp
  lu: number        // last used timestamp
}

// ======================== Constants ========================

const MAX_SKIP = 256
const INFO_RATCHET = new TextEncoder().encode('LYNKSTAR_Ratchet')
const INFO_X3DH = new TextEncoder().encode('LYNKSTAR_X3DH')
export const WIRE_VER = 3

// ======================== Crypto Primitives ========================

export const hmac256 = (k: Uint8Array, d: Uint8Array): Uint8Array => hmac(sha256, k, d)

export function kdfChain(ck: Uint8Array): { mk: Uint8Array; nck: Uint8Array } {
  return {
    mk: hmac256(ck, new Uint8Array([0x01])).slice(0, 32),
    nck: hmac256(ck, new Uint8Array([0x02])).slice(0, 32),
  }
}

export function kdfRoot(rk: Uint8Array, dhOut: Uint8Array): { rk: Uint8Array; ck: Uint8Array } {
  const d = hkdf(sha256, dhOut, rk, INFO_RATCHET, 64)
  return { rk: d.slice(0, 32), ck: d.slice(32, 64) }
}

export const xdh = (sk: Uint8Array, pk: Uint8Array): Uint8Array => nacl.scalarMult(sk, pk)

export function genKP(): KeyPair {
  // Use nacl.box.keyPair which generates X25519 keys
  return nacl.box.keyPair()
}

export function u8eq(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
  return true
}

export function cat(...a: Uint8Array[]): Uint8Array {
  const r = new Uint8Array(a.reduce((s, x) => s + x.length, 0))
  let o = 0
  for (const x of a) { r.set(x, o); o += x.length }
  return r
}

// ======================== Header Encoding ========================

export function encHdr(h: RatchetHeader): Uint8Array {
  const b = new Uint8Array(40)
  b.set(h.dhPub, 0)
  new DataView(b.buffer).setUint32(32, h.pn, false)
  new DataView(b.buffer).setUint32(36, h.n, false)
  return b
}

export function decHdr(d: Uint8Array): RatchetHeader {
  const v = new DataView(d.buffer, d.byteOffset)
  return { dhPub: d.slice(0, 32), pn: v.getUint32(32, false), n: v.getUint32(36, false) }
}

// ======================== Wire Format ========================
// v3: [version(1)][hdrLen(1)][header(40)][nonce(24)][ciphertext(N)]

export function wireEnc(hdr: RatchetHeader, nonce: Uint8Array, ct: Uint8Array): Uint8Array {
  const h = encHdr(hdr)
  const buf = new Uint8Array(2 + h.length + nonce.length + ct.length)
  buf[0] = WIRE_VER; buf[1] = h.length
  buf.set(h, 2)
  buf.set(nonce, 2 + h.length)
  buf.set(ct, 2 + h.length + nonce.length)
  return buf
}

export function wireDec(data: Uint8Array): { hdr: RatchetHeader; nonce: Uint8Array; ct: Uint8Array } | null {
  if (data.length < 2 || data[0] !== WIRE_VER) return null
  const hLen = data[1]
  if (data.length < 2 + hLen + 24) return null
  return {
    hdr: decHdr(data.slice(2, 2 + hLen)),
    nonce: data.slice(2 + hLen, 2 + hLen + 24),
    ct: data.slice(2 + hLen + 24),
  }
}

// ======================== Double Ratchet ========================

export class DoubleRatchet {
  s: DRState

  constructor(s: DRState) { this.s = s }

  /** Initialize as Alice (initiator) - has sending chain from start */
  static alice(sk: Uint8Array, bobDH: Uint8Array): DoubleRatchet {
    const kp = genKP()
    const d = xdh(kp.secretKey, bobDH)
    const { rk, ck } = kdfRoot(sk, d)
    return new DoubleRatchet({
      dhSend: kp, dhRemote: bobDH, rk, sCK: ck, rCK: null,
      sN: 0, rN: 0, pN: 0, mk: {},
    })
  }

  /** Initialize as Bob (responder) - waits for first message */
  static bob(sk: Uint8Array, kp: KeyPair): DoubleRatchet {
    return new DoubleRatchet({
      dhSend: kp, dhRemote: null, rk: sk, sCK: null, rCK: null,
      sN: 0, rN: 0, pN: 0, mk: {},
    })
  }

  encrypt(pt: Uint8Array): { hdr: RatchetHeader; ct: Uint8Array; nonce: Uint8Array } {
    if (!this.s.sCK) throw new Error('No sending chain')
    const { mk, nck } = kdfChain(this.s.sCK)
    this.s.sCK = nck
    const hdr: RatchetHeader = { dhPub: this.s.dhSend.publicKey, pn: this.s.pN, n: this.s.sN }
    this.s.sN++
    const nonce = nacl.randomBytes(24)
    return { hdr, ct: nacl.secretbox(pt, nonce, mk), nonce }
  }

  decrypt(hdr: RatchetHeader, ct: Uint8Array, nonce: Uint8Array): Uint8Array | null {
    // Try skipped keys first
    const kid = encodeBase64(hdr.dhPub) + ':' + hdr.n
    const skk = this.s.mk[kid]
    if (skk) {
      delete this.s.mk[kid]
      return nacl.secretbox.open(ct, nonce, decodeBase64(skk))
    }

    // DH ratchet step if new remote key
    const needR = !this.s.dhRemote || !u8eq(hdr.dhPub, this.s.dhRemote)
    if (needR) {
      if (this.s.rCK && this.s.dhRemote) this.skip(this.s.dhRemote, hdr.pn)
      this.dhStep(hdr.dhPub)
    }

    // Skip to correct message number
    this.skip(hdr.dhPub, hdr.n)
    if (!this.s.rCK) return null

    const { mk, nck } = kdfChain(this.s.rCK)
    this.s.rCK = nck
    this.s.rN = hdr.n + 1
    return nacl.secretbox.open(ct, nonce, mk)
  }

  private skip(pk: Uint8Array, until: number): void {
    if (!this.s.rCK || this.s.rN >= until) return
    const n = Math.min(until - this.s.rN, MAX_SKIP)
    const pkb = encodeBase64(pk)
    for (let i = 0; i < n; i++) {
      const { mk, nck } = kdfChain(this.s.rCK)
      this.s.mk[pkb + ':' + this.s.rN] = encodeBase64(mk)
      this.s.rCK = nck
      this.s.rN++
    }
    // Prune old keys
    const ks = Object.keys(this.s.mk)
    if (ks.length > MAX_SKIP) {
      for (const k of ks.slice(0, ks.length - MAX_SKIP)) delete this.s.mk[k]
    }
  }

  private dhStep(rpk: Uint8Array): void {
    this.s.pN = this.s.sN
    this.s.sN = 0
    this.s.rN = 0
    this.s.dhRemote = rpk

    // Derive receiving chain from current DH + new remote key
    const dr = xdh(this.s.dhSend.secretKey, rpk)
    const rr = kdfRoot(this.s.rk, dr)
    this.s.rk = rr.rk
    this.s.rCK = rr.ck

    // Generate new sending DH key pair and derive sending chain
    this.s.dhSend = genKP()
    const ds = xdh(this.s.dhSend.secretKey, rpk)
    const sr = kdfRoot(this.s.rk, ds)
    this.s.rk = sr.rk
    this.s.sCK = sr.ck
  }
}

// ======================== X3DH ========================

export function x3dhInitiate(
  myIK: KeyPair,
  bundle: PreKeyBundle
): { sk: Uint8Array; ek: Uint8Array; hdr: X3DHHeader } {
  const ek = genKP()
  const dh1 = xdh(myIK.secretKey, bundle.signedPreKey)       // DH(IK_A, SPK_B)
  const dh2 = xdh(ek.secretKey, bundle.identityKey)           // DH(EK_A, IK_B)
  const dh3 = xdh(ek.secretKey, bundle.signedPreKey)           // DH(EK_A, SPK_B)
  let combined = cat(dh1, dh2, dh3)
  if (bundle.oneTimePreKey) {
    const dh4 = xdh(ek.secretKey, bundle.oneTimePreKey)        // DH(EK_A, OPK_B)
    combined = cat(combined, dh4)
  }
  const sk = hkdf(sha256, combined, new Uint8Array(32), INFO_X3DH, 32)
  return {
    sk,
    ek: ek.publicKey,
    hdr: {
      identityKey: myIK.publicKey,
      ephemeralKey: ek.publicKey,
      signedPreKeyId: bundle.signedPreKeyId,
      oneTimePreKeyId: bundle.oneTimePreKeyId,
    },
  }
}

export async function x3dhRespond(
  loadSPK: (id: number) => Promise<SignedPreKey | null>,
  loadOPK: (id: number) => Promise<OneTimePreKey | null>,
  delOPK: (id: number) => Promise<void>,
  myIK: KeyPair,
  hdr: X3DHHeader
): Promise<Uint8Array> {
  const spk = await loadSPK(hdr.signedPreKeyId)
  if (!spk) throw new Error('Signed pre-key not found: ' + hdr.signedPreKeyId)

  const dh1 = xdh(spk.keyPair.secretKey, hdr.identityKey)    // DH(SPK_B, IK_A)
  const dh2 = xdh(myIK.secretKey, hdr.ephemeralKey)           // DH(IK_B, EK_A)
  const dh3 = xdh(spk.keyPair.secretKey, hdr.ephemeralKey)    // DH(SPK_B, EK_A)
  let combined = cat(dh1, dh2, dh3)

  if (hdr.oneTimePreKeyId !== undefined && hdr.oneTimePreKeyId !== null) {
    const opk = await loadOPK(hdr.oneTimePreKeyId)
    if (opk) {
      const dh4 = xdh(opk.keyPair.secretKey, hdr.ephemeralKey) // DH(OPK_B, EK_A)
      combined = cat(combined, dh4)
      await delOPK(hdr.oneTimePreKeyId)
    }
  }

  return hkdf(sha256, combined, new Uint8Array(32), INFO_X3DH, 32)
}

// ======================== Serialization ========================

export function serializeSession(s: SessionState): Record<string, unknown> {
  const r = s.dr
  return {
    rid: s.rid, x3dh: s.x3dh, ts: s.ts, lu: s.lu,
    dr: {
      dhSend: { pub: encodeBase64(r.dhSend.publicKey), sec: encodeBase64(r.dhSend.secretKey) },
      dhRemote: r.dhRemote ? encodeBase64(r.dhRemote) : null,
      rk: encodeBase64(r.rk),
      sCK: r.sCK ? encodeBase64(r.sCK) : null,
      rCK: r.rCK ? encodeBase64(r.rCK) : null,
      sN: r.sN, rN: r.rN, pN: r.pN, mk: r.mk,
    },
  }
}

export function deserializeSession(d: Record<string, unknown>): SessionState {
  const r = d.dr as Record<string, unknown>
  const dk = r.dhSend as Record<string, string>
  return {
    rid: d.rid as string, x3dh: d.x3dh as boolean,
    ts: d.ts as number, lu: d.lu as number,
    dr: {
      dhSend: { publicKey: decodeBase64(dk.pub), secretKey: decodeBase64(dk.sec) },
      dhRemote: r.dhRemote ? decodeBase64(r.dhRemote as string) : null,
      rk: decodeBase64(r.rk as string),
      sCK: r.sCK ? decodeBase64(r.sCK as string) : null,
      rCK: r.rCK ? decodeBase64(r.rCK as string) : null,
      sN: r.sN as number, rN: r.rN as number, pN: r.pN as number,
      mk: (r.mk || {}) as Record<string, string>,
    },
  }
}

// ======================== Key Generation ========================

export function generateIdentityKeyPair(): KeyPair {
  return nacl.box.keyPair()
}

export function generateSignedPreKey(identitySecretKey: Uint8Array, keyId: number): SignedPreKey {
  const kp = nacl.box.keyPair()
  const signKP = nacl.sign.keyPair.fromSeed(identitySecretKey.slice(0, 32))
  const signature = nacl.sign.detached(kp.publicKey, signKP.secretKey)
  return { keyId, keyPair: kp, signature }
}

export function generateOneTimePreKeys(startId: number, count: number): OneTimePreKey[] {
  const keys: OneTimePreKey[] = []
  for (let i = 0; i < count; i++) {
    keys.push({ keyId: startId + i, keyPair: nacl.box.keyPair() })
  }
  return keys
}

// ======================== Fallback Symmetric Session ========================

export function createFallbackDRState(myId: string, theirId: string): DRState {
  const sorted = [myId, theirId].sort()
  const material = new TextEncoder().encode('lynkstar:v5:' + sorted[0] + ':' + sorted[1])
  const sk = hmac256(new TextEncoder().encode('LYNKSTAR_Fallback'), material)
  const kp = genKP()
  const isFirst = sorted[0] === myId
  const simDH = hmac256(sk, new TextEncoder().encode('DH_SIM'))
  const { rk, ck } = kdfRoot(sk, simDH)

  return {
    dhSend: kp, dhRemote: null, rk,
    sCK: isFirst ? ck : sk.slice(0, 32),
    rCK: isFirst ? sk.slice(0, 32) : ck,
    sN: 0, rN: 0, pN: 0, mk: {},
  }
}
